import { zodResolver } from '@hookform/resolvers/zod'
import type { HttpError } from '@refinedev/core'
import { useInvalidate } from '@refinedev/core'
import { useForm } from '@refinedev/react-hook-form'
import type { PlatformSettings } from '@sirius/contracts/admin'
import { PLATFORM_SETTINGS_ID } from '@sirius/contracts/admin'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { useAdminClient } from '~/api/adminClientContext'
import { InheritableSelect } from '~/components/inheritableSelect'
import { ListView, ListViewHeader } from '~/components/refineUi/views/listView'
import { Button } from '~/components/ui/button'
import {
  Form,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '~/components/ui/form'
import {
  ADMIN_LANGUAGE_OPTIONS,
  inheritedAdminLanguageLabel,
  isAdminLanguage,
} from '~/i18n/adminLanguage'
import {
  FORM_RECORD_QUERY_OPTIONS,
  useRecordPending,
} from '~/lib/formRecordQuery'
import { USE_DEFAULT, changedOnly, fromOwn, toOwn } from '~/lib/inheritable'
import {
  LANGUAGE_NAMES,
  LANGUAGE_OPTIONS,
  isCabinetLanguage,
} from '~/pages/cabinets/cabinetLanguage'
import { PLATFORM_SETTINGS_RESOURCE } from '~/providers/dataProvider'
import { refreshMe } from '~/providers/sessionQueries'

/** The default languages as select values. */
const platformSettingsSchema = z.object({
  defaultLanguageChoice: z.string(),
  adminLanguageChoice: z.string(),
})
type PlatformSettingsValues = z.input<typeof platformSettingsSchema>
type PlatformSettingsVariables = Partial<
  Pick<PlatformSettings, 'defaultLanguage' | 'adminLanguage'>
>

/**
 * Platform › Settings (`/platform/settings`, staff only): the platform's
 * defaults, which every tenant on Use default inherits: the language of its
 * cabinets and the language of the panel. The cabinets' own Use default is the
 * built-in English, the panel's the browser's language.
 */
export function PlatformSettingsPage() {
  const { t } = useTranslation()
  const client = useAdminClient()
  const queryClient = useQueryClient()
  const invalidate = useInvalidate()
  const form = useForm<PlatformSettings, HttpError, PlatformSettingsValues>({
    resolver: zodResolver(platformSettingsSchema),
    defaultValues: {
      defaultLanguageChoice: USE_DEFAULT,
      adminLanguageChoice: USE_DEFAULT,
    },
    refineCoreProps: {
      resource: PLATFORM_SETTINGS_RESOURCE,
      action: 'edit',
      id: PLATFORM_SETTINGS_ID,
      redirect: false,
      queryOptions: FORM_RECORD_QUERY_OPTIONS,
      successNotification: () => ({
        type: 'success',
        message: t('platformSettingsPage.savedNotification'),
      }),
      // tenants and cabinets on Use default show the default they inherit;
      // the session names the panel language, which may be the platform's now
      onMutationSuccess: async () => {
        await Promise.all([
          refreshMe(client, queryClient),
          invalidate({ resource: 'tenants', invalidates: ['resourceAll'] }),
          invalidate({ resource: 'cabinets', invalidates: ['resourceAll'] }),
        ])
      },
    },
  })
  const { onFinish, formLoading, query } = form.refineCore
  // nothing may be picked or saved before the record has settled
  const pending = useRecordPending('edit', query)
  const record = query?.data?.data
  const { reset } = form

  // `defaultLanguageChoice` and `adminLanguageChoice` are no fields of the
  // record, so Refine can't fill them in; it must not: Refine would write the
  // record's `defaultLanguage` / `adminLanguage` (null for Use default) over
  // the select's `USE_DEFAULT`
  useEffect(() => {
    if (record !== undefined) {
      reset({
        defaultLanguageChoice: fromOwn(record.defaultLanguage),
        adminLanguageChoice: fromOwn(record.adminLanguage),
      })
    }
  }, [record, reset])

  return (
    <ListView>
      <ListViewHeader
        title={t('platformSettingsPage.title')}
        canCreate={false}
      />
      {query?.error ? (
        <p
          role="alert"
          className="text-destructive text-sm"
        >
          {t('platformSettingsPage.loadError', {
            message: query.error.message,
          })}
        </p>
      ) : (
        <Form {...form}>
          <form
            aria-busy={pending}
            onSubmit={form.handleSubmit(
              ({ defaultLanguageChoice, adminLanguageChoice }) =>
                // only what the user changed: the platform's settings are
                // shared, a save must not put back what someone else set
                // after this page loaded
                onFinish({
                  ...changedOnly(
                    'defaultLanguage',
                    record?.defaultLanguage,
                    toOwn(defaultLanguageChoice, isCabinetLanguage),
                  ),
                  ...changedOnly(
                    'adminLanguage',
                    record?.adminLanguage,
                    toOwn(adminLanguageChoice, isAdminLanguage),
                  ),
                } satisfies PlatformSettingsVariables as never),
            )}
            className="max-w-md space-y-4"
          >
            <FormField
              control={form.control}
              name="defaultLanguageChoice"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t('platformSettingsPage.defaultLanguage.label')}
                  </FormLabel>
                  <InheritableSelect
                    aria-label={t('platformSettingsPage.defaultLanguage.label')}
                    value={field.value}
                    disabled={pending}
                    onChange={field.onChange}
                    options={LANGUAGE_OPTIONS}
                    inheritedLabel={
                      record === undefined
                        ? undefined
                        : LANGUAGE_NAMES[record.inheritedDefaultLanguage]
                    }
                  />
                  <FormDescription>
                    {t('platformSettingsPage.defaultLanguage.hint')}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="adminLanguageChoice"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('shell.adminLanguage.label')}</FormLabel>
                  <InheritableSelect
                    aria-label={t('shell.adminLanguage.label')}
                    value={field.value}
                    disabled={pending}
                    onChange={field.onChange}
                    options={ADMIN_LANGUAGE_OPTIONS}
                    inheritedLabel={inheritedAdminLanguageLabel(
                      record?.inheritedAdminLanguage,
                      t('shell.adminLanguage.browser'),
                    )}
                  />
                  <FormDescription>
                    {t('shell.adminLanguage.platformHint')}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button
              type="submit"
              disabled={formLoading || pending}
            >
              {t('platformSettingsPage.save')}
            </Button>
          </form>
        </Form>
      )}
    </ListView>
  )
}
